import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import { ALL_STATES } from './allStates';
import { MapPin, Briefcase, ArrowRight } from 'lucide-react';

const SPECIALTIES = [
  ['LCSW Jobs', '/lcsw-jobs'],
  ['LMSW Jobs', '/lmsw-jobs'],
  ['LBSW Jobs', '/lbsw-jobs'],
  ['Remote Social Work Jobs', '/remote-social-work-jobs'],
  ['Hospital Social Worker Jobs', '/hospital-social-worker-jobs'],
  ['Hospice Social Worker Jobs', '/hospice-social-worker-jobs'],
  ['School Social Worker Jobs', '/school-social-worker-jobs'],
  ['Behavioral Health Jobs', '/behavioral-health-social-worker-jobs'],
];

const StateJobsHub = () => {
  const entries = Object.entries(ALL_STATES).sort((a, b) => a[1].name.localeCompare(b[1].name));
  const title = 'Social Work Jobs by State | All 50 States + DC';
  const description = 'Find licensed social work jobs by state — LCSW, LMSW and LBSW openings in all 50 states and Washington, DC. Choose your state to see current social work jobs near you.';
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: title,
    description,
    url: 'https://ineedasocialworker.com/social-work-jobs-by-state',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: entries.map(([slug, d], i) => ({
        '@type': 'ListItem', position: i + 1, name: `Social work jobs in ${d.name}`,
        url: `https://ineedasocialworker.com/social-work-jobs/${slug}`,
      })),
    },
  };

  return (
    <div className="min-h-screen bg-[#F7F5EF] pb-16" data-testid="state-jobs-hub">
      <Seo exactTitle={`${title} | I Need A Social Worker`} description={description} path="/social-work-jobs-by-state" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <section className="bg-primary pt-24 pb-12 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-secondary font-semibold uppercase tracking-widest text-xs flex items-center gap-2"><MapPin className="w-4 h-4" /> All 50 states + DC</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mt-2">
            Social Work Jobs <span className="text-secondary">by State</span>
          </h1>
          <p className="mt-4 text-white/85 text-lg max-w-2xl">
            Choose your state to see current LCSW, LMSW, LBSW and social work assistant openings — plus major cities, license filters and local guidance.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
        <h2 className="font-serif text-2xl font-bold text-primary mb-4">Browse every state</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {entries.map(([slug, d]) => (
            <Link key={slug} to={`/social-work-jobs/${slug}`} data-testid={`hub-state-${slug}`}
              className="bg-white rounded-xl border border-gray-100 p-4 hover:border-secondary hover:shadow-md transition-all group flex items-center justify-between">
              <span className="font-semibold text-primary">{d.name}</span>
              <ArrowRight className="w-4 h-4 text-secondary group-hover:translate-x-1 transition-transform" />
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        <h2 className="font-serif text-2xl font-bold text-primary mb-4">Or browse by specialty</h2>
        <div className="flex flex-wrap gap-2">
          {SPECIALTIES.map(([label, to]) => (
            <Link key={to} to={to}
              className="px-4 py-2 rounded-full text-sm bg-white border border-gray-200 text-gray-700 hover:border-secondary hover:text-primary transition-colors flex items-center gap-1">
              <Briefcase className="w-3.5 h-3.5 text-secondary" /> {label}
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        <div className="bg-white rounded-2xl border border-gray-100 p-8">
          <h2 className="font-serif text-2xl font-bold text-primary">Find social work jobs anywhere in the U.S.</h2>
          <p className="text-gray-600 mt-3 leading-relaxed">
            I Need A Social Worker lists licensed social work roles in every state — hospitals, hospice and home health, K-12 schools,
            behavioral health, child welfare, veterans services and community agencies. Select your state above to see current openings,
            filter by LCSW, LMSW or LBSW, and explore jobs in your city.
          </p>
        </div>
      </section>
    </div>
  );
};

export default StateJobsHub;
